<template>
  <div class="relative">
    <!-- Loading overlay -->
    <div
      v-if="loading"
      class="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg"
    >
      <div class="w-full max-w-md">
        <PrimeProgressBar
          mode="indeterminate"
          style="height: 6px"
        ></PrimeProgressBar>
      </div>
    </div>

    <!-- Form content -->
    <PrimeTabs :value="firstFormSection?.name">
      <PrimeTabList v-if="formSectionCount > 1">
        <template
          v-for="(formSection, index) in formSections"
          :key="formSection.name"
        >
          <PrimeTab :value="formSection.name">
            <div class="flex flex-row gap-2">
              <div>
                {{
                  formSection.name !== "default" ? formSection.name : "General"
                }}
              </div>
              <slot name="tab" v-if="$slots.tab" :formSection="formSection" />
            </div>
          </PrimeTab>
        </template>
      </PrimeTabList>
      <PrimeTabPanels>
        <template
          v-for="(formSection, index) in formSections"
          :key="formSection.name"
        >
          <PrimeTabPanel :value="formSection.name">
            <TransitionGroup
              name="field-list"
              tag="div"
              class="@container grid grid-cols-12 gap-1 pt-3 gap-x-2 md:gap-x-4"
              :class="{ 'pointer-events-none opacity-50': loading }"
            >
              <template v-for="field in enabledFields(formSection.name)">
                <template v-if="field.fieldGroup === null">
                  <template
                    v-for="col in field.columns"
                    :key="col.ColumnNamePc"
                  >
                    <FormField
                      :context="{ formType: props.formType }"
                      v-model:value="record[col.ColumnNamePc]"
                      v-model:actual="record[`_${col.ColumnNamePc}`]"
                      v-model:form="record"
                      :dataKey="col.ColumnNamePc"
                      :affectFields="col.SetFieldsAfterSelect"
                      :showAllOptionInSelect="col.ShowAllOptionInSelect"
                      :defaultExpression="col.DefaultExpression"
                      :min="getMinValue(col)"
                      :max="getMaxValue(col)"
                      :showStar="
                        formType !== 'filter'
                          ? (isRequiredField(col) || isRequiredIf(col)) &&
                            props.showStars
                          : undefined
                      "
                      :inputType="getControlTypeMapping(controlType(col))"
                      :auxInputType="auxControlType(col)"
                      :label="col.ColumnLabel"
                      @onEnterPressed="
                        () => {
                          emits('onEnterPressed', {});
                        }
                      "
                      :misc="{
                        fetchOptions: true,
                        displayKey: col.DisplayColumnName,
                        valueKey: col.ValueColumnName,
                        endpoint: col.EndpointRoute,
                        fkSchemaName: col.FkTableSchema,
                        fkTableName: col.FkTableName,
                        endpointId: col.Endpoint,
                        filter: cascadeFilter(
                          col.CascadeFrom,
                          col.ApplyCascadeOnLookupProperty,
                        ),
                        tokenData: tokenData,
                        url: getUploadUrl(),
                        lookupUrl: lookupUrl(col.ColumnNamePc),
                      }"
                      :colSpan="{
                        sm: col.ColSpanSm,
                        md: col.ColSpanMd,
                        lg: col.ColSpanLg,
                        xl: col.ColSpanXl,
                        xxl: col.ColSpanXxl,
                      }"
                      :errors="
                        validationErrors
                          ? [validationErrors[col.ColumnNamePc]]
                          : []
                      "
                      :validation="col.Validation"
                      @change="
                        fieldChangeEventHandler($event, col.ColumnNamePc)
                      "
                      @input="fieldInputEventHandler($event, col.ColumnNamePc)"
                      @blur="fieldBlurEventHandler($event, col.ColumnNamePc)"
                      @multiSelectAllChange="
                        multiSelectAllChangeEventHandler(
                          $event,
                          col.ColumnNamePc,
                        )
                      "
                      :disabled="isDisabled(col)"
                      :searchType="col.SearchType"
                      :id="col.ColumnNamePc"
                      :placeholder="
                        col.Placeholder ? col.Placeholder : col.ColumnLabel
                      "
                      :name="col.ColumnNamePc"
                      :tableId="props.meta.data.Table.Id"
                    >
                      <template #labelAdjacent v-if="$slots.labelAdjacent">
                        <slot name="labelAdjacent" :column="col"> </slot>
                      </template>
                      <template
                        #label="{ star, label }"
                        v-else-if="$slots.label"
                      >
                        <slot
                          name="label"
                          :column="col"
                          :star="star"
                          :label="label"
                        >
                        </slot>
                      </template>
                    </FormField>
                  </template>
                </template>
                <template v-else>
                  <div class="flex flex-col gap-2 col-span-full">
                    <TransitionGroup
                      name="field-list"
                      tag="fieldset"
                      class="@container grid grid-cols-12 gap-2 gap-x-4 p-4 rounded-lg pt-6 border-2 border-[#B0BDC4] mb-4"
                    >
                      <legend
                        class="bg-white p-1 -m-1 text-[14px] text-[#002940] font-semibold flex flex-row gap-1 items-center"
                      >
                        <div>
                          {{ field.fieldGroup }}
                        </div>
                        <slot
                          name="legend"
                          v-if="$slots.legend"
                          :field="field"
                        ></slot>
                      </legend>
                      <template
                        v-for="col in field.columns"
                        :key="col.ColumnNamePc"
                      >
                        <FormField
                          :context="{ formType: props.formType }"
                          v-model:value="record[col.ColumnNamePc]"
                          v-model:actual="record[`_${col.ColumnNamePc}`]"
                          v-model:form="record"
                          :dataKey="col.ColumnNamePc"
                          :min="getMinValue(col)"
                          :max="getMaxValue(col)"
                          :showStar="isRequiredField(col)"
                          :affectFields="col.SetFieldsAfterSelect"
                          :inputType="getControlTypeMapping(controlType(col))"
                          :auxInputType="auxControlType(col)"
                          :defaultExpression="col.DefaultExpression"
                          :showAllOptionInSelect="col.ShowAllOptionInSelect"
                          :label="col.ColumnLabel"
                          :searchType="col.SearchType"
                          :misc="{
                            fetchOptions: true,
                            displayKey: col.DisplayColumnName,
                            valueKey: col.ValueColumnName,
                            endpoint: col.EndpointRoute,
                            fkSchemaName: col.FkTableSchema,
                            fkTableName: col.FkTableName,
                            endpointId: col.Endpoint,
                            filter: cascadeFilter(
                              col.CascadeFrom,
                              col.ApplyCascadeOnLookupProperty,
                            ),
                            tokenData: tokenData,
                            url: getUploadUrl(),
                            lookupUrl: lookupUrl(col.ColumnNamePc),
                          }"
                          :colSpan="{
                            sm: col.ColSpanSm,
                            md: col.ColSpanMd,
                            lg: col.ColSpanLg,
                            xl: col.ColSpanXl,
                            xxl: col.ColSpanXxl,
                          }"
                          :errors="
                            validationErrors
                              ? [validationErrors[col.ColumnNamePc]]
                              : []
                          "
                          :validation="col.Validation"
                          @change="
                            fieldChangeEventHandler($event, col.ColumnNamePc)
                          "
                          @input="
                            fieldInputEventHandler($event, col.ColumnNamePc)
                          "
                          @blur="
                            fieldBlurEventHandler($event, col.ColumnNamePc)
                          "
                          @multiSelectAllChange="
                            multiSelectAllChangeEventHandler(
                              $event,
                              col.ColumnNamePc,
                            )
                          "
                          :disabled="isDisabled(col)"
                          :id="col.ColumnNamePc"
                          :placeholder="
                            col.Placeholder ? col.Placeholder : col.ColumnLabel
                          "
                          :name="col.ColumnNamePc"
                        >
                          <template #labelAdjacent v-if="$slots.labelAdjacent">
                            <slot name="labelAdjacent" :column="col"> </slot>
                          </template>
                          <template
                            #label="{ star, label }"
                            v-else-if="$slots.label"
                          >
                            <slot
                              name="label"
                              :column="col"
                              :star="star"
                              :label="label"
                            >
                            </slot>
                          </template>
                        </FormField>
                      </template>
                    </TransitionGroup>
                  </div>
                </template>
              </template>
              <slot name="formItem" v-if="$slots.formItem"> </slot>
            </TransitionGroup>
          </PrimeTabPanel>
        </template>
      </PrimeTabPanels>
    </PrimeTabs>
  </div>
</template>

<script setup lang="ts">
// import type { IInputEvent, IChangeEvent, IBlurEvent } from './field/interfaces';
import type {
  IFormInputEvent,
  IFormChangeEvent,
  IFormBlurEvent,
} from "~/scad-shared/types/interfaces";
import type { InputType } from "~/scad-shared/types/generic";
import dayjs from "dayjs";
import type { IMetaColumn, IMetaComputed } from "~/scad-shared/types/scad";

const props = withDefaults(
  defineProps<{
    data?: { [key: string]: any };
    loading?: boolean;
    meta: IMetaComputed;
    formType?: "filter" | "edit" | "create" | "readonly";
    showOnlyRequired?: boolean;
    validateOnInput?: boolean;
    disabledFields?: string[];
    validateOnChange?: boolean;
    showStars?: boolean;
    validateOnBlur?: boolean;
    customOnChangeHandlers?: { [key: string]: ChangeHandler };
    disabled?: boolean;
  }>(),
  {
    showOnlyRequired: false,
    formType: "edit",
    loading: false,
    data: () => ({}),
    customOnChangeHandlers: () => ({}),
    validateOnInput: false,
    validateOnChange: false,
    validateOnBlur: false,
    disabled: false,
    showStars: true,
  },
);

const initialData: { [key: string]: any } = ref(props.data);
const data: { [key: string]: any } = ref(props.data);
const record = defineModel<Indexable>("value", {
  default: () => ({}),
});
const originalRecord = defineModel<Indexable | null>("originalRecord", {
  default: null,
});

const columns = computed(() => {
  switch (props.formType) {
    case "create":
      return props.meta.enabledCreateFormColumns;
    case "filter":
      return props.meta.enabledFilterColumns;
    default:
      return props.meta.enabledEditFormColumns;
  }
});

interface ChangeHandler {
  (record: Indexable, newValue: any): void;
}

const configuredChangeHandlers = ref<{ [key: string]: ChangeHandler }>({});

const lookupUrl = computed(() => {
  return (ColumnNamePc: string) => {
    const table =
      props.meta.data.Table.TableType == "VIEW" &&
      props.meta.data.Table.FKTableName
        ? props.meta.data.Table.FKTableName
        : props.meta.data.Table.TableName;

    const url =
      ColumnNamePc in record.value
        ? `/scad/files?TableName=${table}&FilePath=${record.value[ColumnNamePc]}`
        : undefined;

    return url;
  };
});

const defaultValues: Indexable = {};

props.meta.columns.forEach((column) => {
  if (column.ChangeCallback && column.ChangeCallback != "") {
    try {
      configuredChangeHandlers.value[column.ColumnNamePc] = compileChangeCallback(
        column.ChangeCallback
      );
    } catch (e) {
      // Misconfigured change handler
    }
  }
});

props.meta.enabledCreateFormColumns.forEach((column) => {
  if (
    column.DefaultExpression != null &&
    // below line: if there is already data set, do not set the default value
    !(column.ColumnNamePc in props.data) &&
    column.DefaultExpression != undefined &&
    column.DefaultExpression != "" &&
    props.formType == "create"
  ) {
    const value = evaluateValueExpression(column.DefaultExpression);
    defaultValues[column.ColumnNamePc] = value;
  }
});

const changeHandlers = computed<{ [key: string]: ChangeHandler }>(() => {
  return {
    ...configuredChangeHandlers.value,
    ...props.customOnChangeHandlers,
  };
});

onMounted(() => {
  if (props.data) {
    record.value = {
      ...record.value,
      ...props.data,
    };
  }

  if (Object.keys(defaultValues).length > 0) {
    record.value = {
      ...defaultValues,
      ...record.value,
    };
  }
});

const touchedData: { [key: string]: any } = ref({});

const evaluator = new ExpressionEvaluator();

const evaluateExpression: ComputedRef<(expression: string) => boolean> =
  computed(() => {
    return (expression: string) => {
      return evaluator.evaluateExpression(expression, record.value);
    };
  });

const showField = computed<(col: IMetaColumn) => boolean>(() => {
  return (col: IMetaColumn) => {
    const evaluationResult: boolean = col.ShowExpression
      ? evaluateExpression.value(col.ShowExpression)
      : true;

    let isRequiredResult: boolean = true;

    if (props.showOnlyRequired) {
      isRequiredResult = isRequiredField(col);
    }
    return evaluationResult && isRequiredResult;
  };
});

const enabledColumns = computed(() => {
  return columns.value.filter((col: IMetaColumn) => {
    let shouldShowBasedOnFormType;
    let shouldShowBasedOnControlType = true;

    switch (props.formType) {
      case "filter":
        shouldShowBasedOnFormType = col.ShowInFilter;
        shouldShowBasedOnControlType = !["fileuploadsingle"].includes(
          col.ControlType?.toLowerCase() || "",
        );
        break;
      case "create":
        shouldShowBasedOnFormType = col.ShowInCreateForm;
        break;
      case "edit":
        shouldShowBasedOnFormType = col.ShowInEditForm;
        break;
      case "readonly":
        shouldShowBasedOnFormType = col.ShowInEditForm; // Readonly uses same visibility as edit
        break;
      default:
        shouldShowBasedOnFormType = false;
    }

    return (
      showField.value(col) &&
      shouldShowBasedOnFormType &&
      shouldShowBasedOnControlType
    );
  });
});

const enabledFields = computed(() => {
  return (formSection: string | null) => {
    const usedFieldGroups: string[] = [];
    const fields: { fieldGroup: string | null; columns: IMetaColumn[] }[] = [];
    // If formSection is "default then filter by formsection null, else filter by formsection = formSection"
    const columns =
      formSection === "default"
        ? enabledColumns.value.filter(
            (col) =>
              !col.FormSection ||
              col.FormSection === null ||
              col.FormSection === "default",
          )
        : enabledColumns.value.filter((col) => col.FormSection === formSection);

    columns.forEach((col) => {
      if (col.FieldGroup && !usedFieldGroups.includes(col.FieldGroup)) {
        usedFieldGroups.push(col.FieldGroup);
        fields.push({
          fieldGroup: col.FieldGroup,
          columns: columns.filter((c: IMetaColumn) => {
            return c.FieldGroup == col.FieldGroup;
          }),
        });
      } else if (!col.FieldGroup) {
        fields.push({
          fieldGroup: null,
          columns: [col],
        });
      }
    });

    return fields;
  };
});

const tokenData: ComputedRef<{ [key: string]: any }> = computed(() => {
  const selfInserts: { [key: string]: any } = {};

  props.meta.data.PKeys.forEach((pKey) => {
    selfInserts[`${props.meta.data.Table.TableName}${pKey.ColumnNamePc}`] =
      record.value[pKey.ColumnNamePc];
  });

  return { ...record.value, ...selfInserts };
});

const cascadeFilter = computed(() => {
  return (cascadeFrom: string, applyCascadeOnLookupProperty?: string) => {
    if (!applyCascadeOnLookupProperty) {
      applyCascadeOnLookupProperty = undefined;
    }
    const filter: { [key: string]: any } = {};
    if (!cascadeFrom) return filter;
    const cascades = [];
    if (cascadeFrom.includes(",")) {
      cascades.push(...cascadeFrom.split(","));
    } else {
      cascades.push(cascadeFrom);
    }

    cascades.forEach((cascade) => {
      let selectedValue: any = cascade
        .trim()
        .split(".")
        .reduce((a, b) => a[b], record.value);

      if (selectedValue == undefined || selectedValue == null) {
        //no selection has been made in the parent control yet
        //so apply a filter value of zero on the cascade property, the idea being that this should result in no records being returned
        //console.log('cascading on 0', cascadeFrom);
        filter[applyCascadeOnLookupProperty ?? cascade] = 0;
      } else if (selectedValue === 0) {
        // filter[applyCascadeOnLookupProperty ?? cascade] = null;
        //the user has selected an All option.
        // so no need to apply a filter at all
        //console.log('cascading on all', cascade);
      } else {
        //the user has selected an option in the cascade parent
        //console.log('cascading on ', cascade,record.value[cascade]);
        filter[applyCascadeOnLookupProperty ?? cascade] = record.value[cascade];
      }
    });

    return filter;
  };
});

function controlType(col: IMetaColumn): string {
  const controlType =
    props.formType == "filter" && col.FilterControlType
      ? col.FilterControlType
      : col.ControlType;

  const segments = controlType.split(":");

  return segments[0];
}

function auxControlType(col: IMetaColumn): string | undefined {
  const controlType =
    props.formType == "filter" && col.FilterControlType
      ? col.FilterControlType
      : col.ControlType;

  const segments = controlType.split(":");

  return segments.length > 1 ? (segments[1] as string) : undefined;
}

function getControlTypeMapping(ControlType: string): InputType | undefined {
  // @ts-ignore
  return ControlTypeToGenericInputType(ControlType);
}

const excludedValidations: { [key: string]: string[] } = {
  edit: ["afterToday"],
  create: [],
  filter: [],
  readonly: [],
};

const validationRulesMap = computed(() => {
  return getValidationRulesMapFromMetaColumns(
    columns.value,
    "Validation",
    excludedValidations[props.formType],
  );
});
const validationRules = computed<ValidationRule[]>(() => {
  const rules: ValidationRule[] = [];

  enabledColumns.value
    .filter((col) => !isDisabled(col))
    .forEach((col) => {
      if (validationRulesMap.value[col.ColumnNamePc]) {
        rules.push(validationRulesMap.value[col.ColumnNamePc]);
      }
    });

  return rules;
});

const optionalValidationRulesMap = computed(() => {
  return getValidationRulesMapFromMetaColumns(
    columns.value,
    "OptionalValidation",
  );
});
const optionalValidationRules = computed<ValidationRule[]>(() => {
  const rules: ValidationRule[] = [];

  enabledColumns.value.forEach((col) => {
    if (optionalValidationRulesMap.value[col.ColumnNamePc]) {
      rules.push(optionalValidationRulesMap.value[col.ColumnNamePc]);
    }
  });

  return rules;
});

const optionalValidation = computed(() => {
  return new ValidationEngine(optionalValidationRules.value);
});

const validation = computed(() => {
  return new ValidationEngine(validationRules.value);
});

function isRequiredField(field: IMetaColumn): boolean {
  let isRequired: boolean = false;

  if (
    field.Validation &&
    !isRequired &&
    field.ColumnNamePc in validationRulesMap.value
  ) {
    isRequired =
      !validationRulesMap.value[field.ColumnNamePc].schema.spec.optional;
  }
  if (
    field.OptionalValidation &&
    !isRequired &&
    field.ColumnNamePc in optionalValidationRulesMap.value
  ) {
    isRequired =
      !optionalValidationRulesMap.value[field.ColumnNamePc].schema.spec
        .optional;
  }

  return isRequired;
}

const isRequiredIf = computed(() => {
  return (field: IMetaColumn): boolean => {
    if (field.Validation) {
      const requiredIfValidation = (field.Validation as string[]).find(
        (validationEntry) => validationEntry.includes("requiredIf,"),
      );
      if (requiredIfValidation) {
        const entryAsArray = requiredIfValidation
          .replace(/\[|\]/g, "")
          .split(",");

        return evaluateExpression.value(entryAsArray[1].trim());
      }
    }
    return false;
  };
});

function getMinValue(field: IMetaColumn): any | null {
  if (field.Validation) {
    //console.log(`minValue for ${field.ColumnNamePc}:`, field.Validation);
    const minValidation = (field.Validation as string[]).find(
      (validationEntry) => validationEntry.includes("min"),
    );
    let betweenValidation;

    if (!minValidation) {
      //console.log(`minValue for ${field.ColumnNamePc}: no min validation`);
      betweenValidation = (field.Validation as string[]).find((validation) =>
        validation.includes("between"),
      );
      if (!betweenValidation) {
        //console.log(`minValue for ${field.ColumnNamePc}: no min nor between validation`);
        return null;
      }
    }

    const entryAsArray = ((betweenValidation ?? "") || (minValidation ?? ""))
      .replace(/\[|\]/g, "")
      .split(",");
    //console.log(`minValue for ${field.ColumnNamePc}: minValueAsArray `, entryAsArray);
    let minValue: Date | string = entryAsArray[1].trim();
    //console.log(`minValue for ${field.ColumnNamePc}: configured minValue is `, minValue);
    if (minValue.charAt(0) === "@") {
      // console.log(`minValue for ${field.ColumnNamePc}: the value is a reference to another field...not supported`);
      return null;
    }
    if (minValue.charAt(0) === "$") {
      //the value is a static value
      //if we are dealing with a date, we need to convert the static value to a date
      //else just return the static value
      const hasDateValidation = (field.Validation as string[]).some(
        (validation) => validation === "[date]",
      );
      if (hasDateValidation) {
        //console.log(`minValue for ${field.ColumnNamePc}: we are dealing with a date`, minValue);
        minValue = convertDateExpressionToDate(minValue.slice(1));
      } else {
        minValue = minValue.slice(1);
      }
    }
    //console.log(`minValue for ${field.ColumnNamePc}: returning minValue`, minValue);
    return minValue;
  } else {
    //console.log(`minValue for ${field.ColumnNamePc} just null`);
    return null;
  }
}

function getMaxValue(field: IMetaColumn): any {
  if (field.Validation) {
    //console.log(`maxValue for ${field.ColumnNamePc}:`, field.Validation);
    const minValidation = (field.Validation as string[]).find(
      (validationEntry: any) => validationEntry.includes("max"),
    );
    let betweenValidation;

    if (!minValidation) {
      //console.log(`maxValue for ${field.ColumnNamePc}: no min validation`);
      betweenValidation = (field.Validation as string[]).find(
        (validation: any) => validation.includes("between"),
      );
      if (!betweenValidation) {
        //console.log(`maxValue for ${field.ColumnNamePc}: no min nor between validation`);
        return null;
      }
    }

    const entryAsArray = ((betweenValidation ?? "") || (minValidation ?? ""))
      .replace(/\[|\]/g, "")
      .split(",");
    // console.log(
    //   `maxValue for ${field.ColumnNamePc}: maxValueAsArray `,
    //   entryAsArray[0]
    // );
    let maxValue: Date | string = betweenValidation
      ? entryAsArray[1].trim()
      : entryAsArray[0].trim();
    //console.log(`maxValue for ${field.ColumnNamePc}: configured maxValue is `, maxValue);
    if (maxValue.charAt(0) === "@") {
      // console.log(`maxValue for ${field.ColumnNamePc}: the value is a reference to another field...not supported`);
      return null;
    }
    if (maxValue.charAt(0) === "$") {
      //the value is a static value
      //if we are dealing with a date, we need to convert the static value to a date
      //else just return the static value
      const hasDateValidation = (field.Validation as string[]).some(
        (validation) => validation === "[date]",
      );
      if (hasDateValidation) {
        //console.log(`maxValue for ${field.ColumnNamePc}: we are dealing with a date`, maxValue);
        maxValue = convertDateExpressionToDate(maxValue.slice(1));
      } else {
        maxValue = maxValue.slice(1);
      }
    }
    //console.log(`maxValue for ${field.ColumnNamePc}: returning maxValue`, maxValue);
    return maxValue;
  } else {
    //console.log(`maxValue for ${field.ColumnNamePc} just null`);
    return null;
  }
}

const validationErrors = defineModel<{ [key: string]: string }>(
  "validationErrors",
  {
    default: {},
  },
);

const emits = defineEmits<{
  change: [event: IFormInputEvent];
  input: [event: IFormChangeEvent];
  blur: [event: IFormBlurEvent];
  onEnterPressed: [event: any];
}>();

async function validate(throwOnError: boolean = false) {
  const validationRecord = { ...record.value };
  if (originalRecord.value) {
    validationRecord["_originalRecord"] = originalRecord.value;
  }

  validationErrors.value =
    await validation.value.validateToString(validationRecord);

  if (throwOnError && Object.keys(validationErrors.value).length > 0) {
    let errors: string[] = [];
    for (let field in validationErrors.value) {
      errors.push(validationErrors.value[field]);
    }
    throw new Error(errors.join(".\n ") + ".");
  }
  return validationErrors.value;
}

defineExpose({
  validate,
});

async function multiSelectAllChangeEventHandler(
  $event: boolean,
  columnName: string,
) {
  //console.log('multiSelectAllChangeEventHandler', columnName, $event);
  columnName = `All${columnName}`;
  if ($event !== initialData.value[columnName]) {
    touchedData.value[columnName] = $event;
  } else if (columnName in touchedData.value) {
    delete touchedData.value[columnName];
  }
  record.value[columnName] = $event;
  emits("change", {
    data: record.value,
    field: columnName,
    touchedData: touchedData.value,
  });
  // if (props.validateOnChange) {
  //   validationErrors.value = await validation.validateToString(record.value);
  // }
}

async function fieldChangeEventHandler($event: any, columnName: string) {
  if (columnName in changeHandlers.value) {
    changeHandlers.value[columnName](record.value, $event.value);
  }

  if ($event.value !== initialData.value[columnName]) {
    touchedData.value[columnName] = $event.value;
  } else if (columnName in touchedData.value) {
    delete touchedData.value[columnName];
  }
  emits("change", {
    data: record.value,
    field: columnName,
    touchedData: touchedData.value,
  });
  if (props.validateOnChange) {
    validationErrors.value = await validation.value.validateToString(
      record.value,
    );
  }
  const column = props.meta.columnsMap[columnName];
  if (column && column.OnChangeComputationEndpoint) {
    try {
      const changeEndpoint = processSlugs(
        column.OnChangeComputationEndpoint,
        tokenData.value,
      );
      const changeResponse = await $fetch(
        `/scad${changeEndpoint.startsWith("/") ? "" : "/"}${changeEndpoint}`,
        {
          method: "PUT",
          body: JSON.stringify({
            RecordContext: record.value,
          }),
        },
      );

      if (changeResponse) {
        for (const key in changeResponse) {
          record.value[key] = changeResponse[key];
        }
      }
    } catch (error) {
      console.error(
        `Error in onChange computation for ${column.ColumnNamePc}:`,
        error,
      );
    }
  }
}

async function fieldInputEventHandler($event: any, columnName: string) {
  touchedData.value[columnName] = $event.value;
  emits("input", {
    data: record.value,
    field: columnName,
    touchedData: touchedData.value,
  });
  if (props.validateOnInput) {
    validationErrors.value = await validation.value.validateToString(
      record.value,
    );
  }
}
async function fieldBlurEventHandler($event: any, columnName: string) {
  touchedData.value[columnName] = $event.value;
  emits("blur", {
    data: record.value,
    field: columnName,
    touchedData: touchedData.value,
  });
  if (props.validateOnBlur) {
    validationErrors.value = await validation.value.validateToString(
      record.value,
    );
  }
}

function isDisabled(col: IMetaColumn): boolean {
  if (props.disabledFields && props.disabledFields.includes(col.ColumnNamePc))
    return true;
  if (props.formType === "readonly" || props.disabled) return true;
  if (props.formType === "filter") return false;

  const mutabilityCheck = !(
    (props.formType === "edit" && col.Mutable) ||
    ["filter", "create"].includes(props.formType)
  );
  return (
    col.IsIdentity ||
    !col.IsEnabled ||
    col.Computed ||
    mutabilityCheck ||
    (col.DisabledExpression
      ? evaluateExpression.value(col.DisabledExpression)
      : false)
  );
}

function getUploadUrl() {
  const table =
    props.meta.data.Table.TableType != "VIEW"
      ? props.meta.data.Table.TableName
      : props.meta.data.Table.FKTableName;

  // console.log(table, props.meta.data.Table);
  if ("Id" in record.value) {
    return table ? `/scad/${Pluralize(table)}/${record.value.Id}` : undefined;
  } else {
    return table ? `/scad/${Pluralize(table)}/0` : undefined;
  }
}

// Form section logic
const formSections = computed(() => {
  const sections: { [key: string]: { columns: IMetaColumn[]; name: string } } =
    {};

  enabledColumns.value.forEach((col) => {
    const sectionName = col.FormSection || "default";
    if (!sections[sectionName]) {
      sections[sectionName] = { columns: [], name: sectionName };
    }
    sections[sectionName].columns.push(col);
  });

  return sections;
});

const formSectionCount = computed(() => Object.keys(formSections.value).length);

const firstFormSection = computed(() => {
  const sectionNames = Object.keys(formSections.value);
  return formSections.value[sectionNames?.[0]] ?? null;
});
</script>

<style scoped>
.field-list-move {
  transition: transform 0.3s ease;
}

.field-card {
  transition:
    box-shadow 0.2s ease,
    opacity 0.2s ease;
}

.field-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
</style>
